import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Plus, BookOpen, Trash2, Edit2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const SUBJECT_COLORS = ['#6366F1', '#EC4899', '#14B8A6', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444'];
const SUBJECT_ICONS = ['📚', '🔬', '🎨', '⚽', '🎵', '💻', '🌍', '📐', '🏛️'];

const SubjectsPage = () => {
  const { getAuthHeader } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [topics, setTopics] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [showDialog, setShowDialog] = useState(false);
  const [showTopicDialog, setShowTopicDialog] = useState(false);
  const [currentSubjectId, setCurrentSubjectId] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: SUBJECT_COLORS[0], icon: SUBJECT_ICONS[0] });
  const [topicData, setTopicData] = useState({ name: '', difficulty: 'medium' });

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const headers = getAuthHeader();
      const response = await api.getSubjects(headers);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      toast.error('Fehler beim Laden der Fächer');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (subjectId) => {
    try {
      const headers = getAuthHeader();
      const response = await api.getTopics(subjectId, headers);
      setTopics(prev => ({ ...prev, [subjectId]: response.data }));
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Fehler beim Laden der Themen');
    }
  };

  const toggleSubject = (subjectId) => {
    if (expandedSubject === subjectId) {
      setExpandedSubject(null);
    } else {
      setExpandedSubject(subjectId);
      if (!topics[subjectId]) {
        fetchTopics(subjectId);
      }
    }
  };

  const handleCreateSubject = async () => {
    if (!formData.name.trim()) {
      toast.error('Bitte gib einen Namen ein');
      return;
    }

    try {
      const headers = getAuthHeader();
      await api.createSubject(formData, headers);
      toast.success('Fach erfolgreich erstellt!');
      setShowDialog(false);
      setFormData({ name: '', color: SUBJECT_COLORS[0], icon: SUBJECT_ICONS[0] });
      fetchSubjects();
    } catch (error) {
      console.error('Error creating subject:', error);
      toast.error('Fehler beim Erstellen des Fachs');
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!window.confirm('Möchtest du dieses Fach wirklich löschen? Alle Themen werden ebenfalls gelöscht.')) return;

    try {
      const headers = getAuthHeader();
      await api.deleteSubject(subjectId, headers);
      toast.success('Fach gelöscht');
      fetchSubjects();
    } catch (error) {
      console.error('Error deleting subject:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleCreateTopic = async () => {
    if (!topicData.name.trim()) {
      toast.error('Bitte gib einen Themennamen ein');
      return;
    }

    try {
      const headers = getAuthHeader();
      await api.createTopic(currentSubjectId, topicData, headers);
      toast.success('Thema hinzugefügt!');
      setShowTopicDialog(false);
      setTopicData({ name: '', difficulty: 'medium' });
      fetchTopics(currentSubjectId);
    } catch (error) {
      console.error('Error creating topic:', error);
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const toggleTopicCompletion = async (topicId, currentStatus) => {
    try {
      const headers = getAuthHeader();
      await api.updateTopic(topicId, !currentStatus, headers);
      
      // Update local state
      const subjectId = expandedSubject;
      setTopics(prev => ({
        ...prev,
        [subjectId]: prev[subjectId].map(t => 
          t.id === topicId ? { ...t, completed: !currentStatus } : t
        )
      }));
      
      if (!currentStatus) {
        toast.success('Thema abgeschlossen! +10 Punkte');
      }
    } catch (error) {
      console.error('Error updating topic:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Fächer</h1>
          <p className="text-lg text-slate-600">Verwalte deine Schulfächer und Themen</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-subject-button" className="rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold px-6 shadow-[0_4px_14px_0_rgba(99,102,241,0.39)] hover:shadow-[0_6px_20px_rgba(99,102,241,0.5)] hover:-translate-y-0.5 transition-all duration-200">
              <Plus size={20} className="mr-2" />
              Fach hinzufügen
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">Neues Fach</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fachname</label>
                <Input
                  placeholder="z.B. Mathematik"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="subject-name-input"
                  className="h-12 rounded-2xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Farbe</label>
                <div className="flex gap-2 flex-wrap">
                  {SUBJECT_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-12 h-12 rounded-full border-4 transition-all duration-200 ${
                        formData.color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Icon</label>
                <div className="flex gap-2 flex-wrap">
                  {SUBJECT_ICONS.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`w-12 h-12 text-2xl rounded-xl border-2 transition-all duration-200 hover:scale-110 ${
                        formData.icon === icon ? 'border-primary-600 bg-primary-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`icon-${icon}`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <Button
                onClick={handleCreateSubject}
                data-testid="create-subject-button"
                className="w-full h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold"
              >
                Fach erstellen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {subjects.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-slate-100">
          <BookOpen className="mx-auto text-slate-300 mb-4" size={64} />
          <p className="text-slate-600 text-lg">Noch keine Fächer hinzugefügt</p>
          <p className="text-slate-500 text-sm mt-2">Füge dein erstes Fach hinzu, um loszulegen!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {subjects.map(subject => (
            <div key={subject.id} className="bg-white rounded-3xl border-2 border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300" data-testid={`subject-${subject.id}`}>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 cursor-pointer" onClick={() => toggleSubject(subject.id)}>
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-sm"
                      style={{ backgroundColor: subject.color + '20', color: subject.color }}
                    >
                      {subject.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-heading font-bold text-slate-900">{subject.name}</h3>
                      <p className="text-sm text-slate-600">
                        {topics[subject.id] ? `${topics[subject.id].filter(t => t.completed).length}/${topics[subject.id].length} Themen abgeschlossen` : 'Klicken für Themen'}
                      </p>
                    </div>
                    <button data-testid={`expand-subject-${subject.id}`}>
                      {expandedSubject === subject.id ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteSubject(subject.id)}
                      data-testid={`delete-subject-${subject.id}`}
                      className="text-red-600 hover:bg-red-50 rounded-xl"
                    >
                      <Trash2 size={20} />
                    </Button>
                  </div>
                </div>

                {expandedSubject === subject.id && (
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-heading font-bold text-slate-900">Themen</h4>
                      <Dialog open={showTopicDialog && currentSubjectId === subject.id} onOpenChange={(open) => {
                        setShowTopicDialog(open);
                        if (open) setCurrentSubjectId(subject.id);
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            data-testid={`add-topic-button-${subject.id}`}
                            className="rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold"
                          >
                            <Plus size={16} className="mr-1" />
                            Thema
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-3xl">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-heading font-bold">Neues Thema</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Themenname</label>
                              <Input
                                placeholder="z.B. Quadratische Gleichungen"
                                value={topicData.name}
                                onChange={(e) => setTopicData({ ...topicData, name: e.target.value })}
                                data-testid="topic-name-input"
                                className="h-12 rounded-2xl"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Schwierigkeit</label>
                              <Select value={topicData.difficulty} onValueChange={(value) => setTopicData({ ...topicData, difficulty: value })}>
                                <SelectTrigger data-testid="topic-difficulty-select" className="h-12 rounded-2xl">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="easy">Einfach</SelectItem>
                                  <SelectItem value="medium">Mittel</SelectItem>
                                  <SelectItem value="hard">Schwer</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              onClick={handleCreateTopic}
                              data-testid="create-topic-button"
                              className="w-full h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold"
                            >
                              Thema hinzufügen
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    {topics[subject.id] && topics[subject.id].length > 0 ? (
                      <div className="space-y-2">
                        {topics[subject.id].map(topic => (
                          <div
                            key={topic.id}
                            data-testid={`topic-${topic.id}`}
                            className={`p-4 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
                              topic.completed
                                ? 'bg-green-50 border-green-200'
                                : 'bg-slate-50 border-slate-100 hover:border-primary-300'
                            }`}
                            onClick={() => toggleTopicCompletion(topic.id, topic.completed)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className={`font-bold ${topic.completed ? 'text-green-900 line-through' : 'text-slate-900'}`}>
                                  {topic.name}
                                </p>
                                <p className="text-xs text-slate-600 mt-1">
                                  Schwierigkeit: {topic.difficulty === 'easy' ? 'Einfach' : topic.difficulty === 'medium' ? 'Mittel' : 'Schwer'}
                                </p>
                              </div>
                              <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                                topic.completed ? 'bg-green-200 text-green-800' : 'bg-slate-200 text-slate-700'
                              }`}>
                                {topic.completed ? '✓ Erledigt' : 'Offen'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-8">Noch keine Themen</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubjectsPage;
